import { DownloadIcon } from '@chakra-ui/icons';
import { formatISO } from 'date-fns';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { PageLayout } from '@/components/templates/PageLayout';
import {
  CmBox,
  CmButton,
  CmCard,
  CmScrollBox,
  CmStack,
  CmTable,
  CmTableBodyRow,
  CmTableHeader,
  CmTbody,
  CmTd,
  CmTh,
} from '@/components/uiParts';
import {
  OrdersDataCSVTypes,
  OrdersDataDateTypesV2,
} from '@/domain/models/Order/ordersData';
import { redirect404Page } from '@/lib/AccessControl';
import { CSVOrderFilterForm } from '@/pages/ordersData/csv-v2/OrderFilterForm';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import {
  MappedCSVJobItem,
  TriggerCSVGenerationParams,
  useListCSVJobs,
  useTriggerCSVGeneration,
} from '@/services/OrdersData/hooks';

interface OrdersDataFilter {
  from: Date;
  to: Date;
  startHour: number;
  startMinutes: number;
  endHour: number;
  endMinutes: number;
  selectedCSVType: string;
  selectedDateTimeType: string;
}

const CSVExportPageV2: React.FC = () => {
  const { managerAccountMe } = useManagerAccountMe();
  const history = useHistory();
  const {
    triggerCSVGeneration,
    isLoading: isLoadingGeneration,
    isSuccess,
  } = useTriggerCSVGeneration();
  const {
    csvJobs,
    isLoading: isLoadingJobs,
    refetchCSVJobs,
  } = useListCSVJobs();

  useEffect(() => {
    redirect404Page('order_csv_export', history, managerAccountMe);
  }, [history, managerAccountMe]);

  const handleFilterSubmit = (filter: OrdersDataFilter) => {
    const params: TriggerCSVGenerationParams = {
      date_and_time_from: formatISO(filter.from),
      date_and_time_to: formatISO(filter.to),
      date_time_type:
        filter.selectedDateTimeType === OrdersDataDateTypesV2.OrderDateTime
          ? 'order_date'
          : 'estimated_pickup_time',
      csv_type:
        filter.selectedCSVType === OrdersDataCSVTypes.OrdersList
          ? 'order'
          : 'order_item',
    };

    triggerCSVGeneration(params);
  };

  useEffect(() => {
    if (isSuccess) {
      refetchCSVJobs();
    }
  }, [isSuccess, refetchCSVJobs]);

  const downloadButtonStyle = {
    width: '40px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0',
    borderRadius: '4px',
    margin: '0 auto',
  };

  const cellStyle = {
    textAlign: 'center',
    borderRight: '1px solid #B3B3B3',
    padding: '12px',
  };

  const isLoading = isLoadingGeneration || isLoadingJobs;

  return (
    <PageLayout title={routes.OrdersDataV2.title}>
      <CmBox>
        <CmStack spacing={8} mt="25px">
          <CSVOrderFilterForm
            dateTypes={OrdersDataDateTypesV2}
            onSubmit={handleFilterSubmit}
            onRefetchCSVJobs={refetchCSVJobs}
            isLoading={isLoading}
          />

          <CmCard padding="20px">
            <CmScrollBox>
              <CmTable variant="simple">
                <CmTableHeader>
                  <CmTh sx={cellStyle}>作成日時</CmTh>
                  <CmTh sx={cellStyle}>状態</CmTh>
                  <CmTh sx={cellStyle}>有効期限</CmTh>
                  <CmTh sx={cellStyle}>ダウンロード</CmTh>
                </CmTableHeader>
                <CmTbody>
                  {csvJobs.map((item: MappedCSVJobItem) => (
                    <CmTableBodyRow
                      key={item.id}
                      bg={item.status === 'completed' ? '#6EDB62' : undefined}
                      borderBottom="1px solid #B3B3B3"
                    >
                      <CmTd sx={cellStyle}>{item.createdAt}</CmTd>
                      <CmTd sx={cellStyle}>{item.statusText}</CmTd>
                      <CmTd sx={cellStyle}>{item.expiryDate}</CmTd>
                      <CmTd sx={cellStyle}>
                        {item.downloadUrl ? (
                          <CmButton
                            as="a"
                            href={item.downloadUrl}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outline"
                            sx={downloadButtonStyle}
                          >
                            <DownloadIcon w={6} h={6} />
                          </CmButton>
                        ) : (
                          <CmButton
                            variant="outline"
                            isDisabled
                            sx={downloadButtonStyle}
                          >
                            <DownloadIcon w={6} h={6} />
                          </CmButton>
                        )}
                      </CmTd>
                    </CmTableBodyRow>
                  ))}
                </CmTbody>
              </CmTable>
            </CmScrollBox>
          </CmCard>
        </CmStack>
      </CmBox>
    </PageLayout>
  );
};

export default CSVExportPageV2;
